<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { formatDate } from '@/utils/dateUtils';
import { STATUS_CLOSED, STATUS_OPEN } from '@/constants';
import Checkbox from 'primevue/checkbox';
import DataTable from 'primevue/datatable';
import TableColumn from 'primevue/column';
import ToggleSwitch from 'primevue/toggleswitch';

interface Props {
  option: object;
  expandedRows?: object[];
}
const props = defineProps<Props>();

const emits = defineEmits<{ 'update:expandedRows': [value: any] }>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();

const rowExpandValue = computed({
  get() {
    return props.expandedRows;
  },
  set(value) {
    emits('update:expandedRows', value);
  }
});
</script>
<template>
  <div class="flight-log">
    <slot></slot>
    <div class="flight-log-detail">
      <DataTable v-model:expandedRows="rowExpandValue" :value="option.flightLog" class="w-full" editMode="row" showGridlines data-key="id">
        <TableColumn :expander="true" class="w-3rem" />
        <TableColumn field="ruleType" :header="t('management.package.type')" :showAddButton="false" :showFilterOperator="false">
          <template #body="{ data, field }">
            <span class="long-text" v-tooltip="data[field]">
              {{ data[field] }}
            </span>
          </template>
        </TableColumn>
        <TableColumn field="gateCode" :showAddButton="false" :showFilterOperator="false">
          <template #header>
            <div class="flex flex-col">
              <span>{{ t('management.package.gateway') }}</span>
              <span>({{ t('management.package.code') }})</span>
            </div>
          </template>
          <template #body="{ data }">
            <div class="flex flex-col">
              <span>{{ data.gateName }}</span>
              <span v-if="data.gateCode">({{ data.gateCode }})</span>
            </div>
          </template>
        </TableColumn>
        <TableColumn field="desCode" :showAddButton="false" :showFilterOperator="false">
          <template #header>
            <div class="flex flex-col">
              <span>{{ t('management.package.destination') }}</span>
              <span>({{ t('management.package.code') }})</span>
            </div>
          </template>
          <template #body="{ data }">
            <div class="flex flex-col">
              <span>{{ data.desName }}</span>
              <span v-if="data.desCode">({{ data.desCode }})</span>
            </div>
          </template>
        </TableColumn>
        <TableColumn class="white-space-nowrap" field="minDuration" :header="t('management.package.min_duration')" :showAddButton="false" :showFilterOperator="false" />
        <TableColumn class="white-space-nowrap" field="maxDuration" :header="t('management.package.max_duration')" :showAddButton="false" :showFilterOperator="false" />
        <TableColumn class="white-space-nowrap" field="minPax" :header="t('management.package.min_pax')" :showAddButton="false" :showFilterOperator="false" />
        <TableColumn class="white-space-nowrap" field="maxPax" :header="t('management.package.max_pax')" :showAddButton="false" :showFilterOperator="false" />
        <TableColumn class="white-space-nowrap" field="minFClass" :showAddButton="false" :showFilterOperator="false">
          <template #header>
            <span>{{ t('management.package.min_f_class') }} ( % )</span>
          </template>
          <template #body="{ data, field }">
            <span v-if="data[field] !== null">{{ data[field] }} %</span>
          </template>
        </TableColumn>
        <TableColumn class="white-space-nowrap" field="maxYClass" :showAddButton="false" :showFilterOperator="false">
          <template #header>
            <span>{{ t('management.package.max_y_class') }} ( % )</span>
          </template>
          <template #body="{ data, field }">
            <span v-if="data[field] !== null">{{ data[field] }} %</span>
          </template>
        </TableColumn>
        <TableColumn class="white-space-nowrap" field="depDateStart" :header="t('management.package.departure_date_start')" :showAddButton="false" :showFilterOperator="false">
          <template #body="{ data }">
            {{ formatDate(data.depDateStart, 'dd-LLL-yyyy', false, currentLanguage) }}
          </template>
        </TableColumn>
        <TableColumn class="white-space-nowrap" field="depDateEnd" :header="t('management.package.departure_date_end')" :showAddButton="false" :showFilterOperator="false">
          <template #body="{ data }">
            {{ formatDate(data.depDateEnd, 'dd-LLL-yyyy', false, currentLanguage) }}
          </template>
        </TableColumn>
        <TableColumn class="white-space-nowrap" field="minCost" :header="t('management.package.minimum_cost')" :showAddButton="false" :showFilterOperator="false" />
        <TableColumn class="cost-rule" field="costRule" :header="$t('management.package.apply_cost_rule')" :showAddButton="false" :showFilterOperator="false">
          <template #body="{ data }">
            <div class="flex justify-content-center">
              <Checkbox :disabled="true" v-model="data.costRule" :binary="true" />
            </div>
          </template>
        </TableColumn>
        <TableColumn class="rule-connector" field="connectorRule" :header="$t('management.package.apply_rule_connector')" :showAddButton="false" :showFilterOperator="false">
          <template #body="{ data }">
            <div class="flex justify-content-center">
              <Checkbox :disabled="true" v-model="data.connectorRule" :binary="true" />
            </div>
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="ruleStatus" :header="t('common.status')">
          <template #body="{ data, field }">
            <ToggleSwitch disabled v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" />
          </template>
        </TableColumn>
        <template #expansion="slotProps">
          <div class="p-3">
            <h5>{{ t('common.flight_details') }}</h5>
            <DataTable :value="slotProps.data.rClass" class="w-full" showGridlines>
              <TableColumn field="flightNo" :header="t('rule.flight_rule.flight_number')" dataType="string" class="min-w-10rem">
                <template #body="{ data }">
                  {{ data.flightNo }}
                </template>
              </TableColumn>
              <TableColumn field="gate" :header="t('worksheet.departure_city')" dataType="string" class="min-w-10rem">
                <template #body="{ data }">
                  {{ data.gate }}
                </template>
              </TableColumn>
              <TableColumn field="dest" :header="t('worksheet.arrival_city')" dataType="string" class="min-w-10rem">
                <template #body="{ data }">
                  {{ data.dest }}
                </template>
              </TableColumn>
              <TableColumn field="depDate" :header="t('worksheet.dep_date')" class="min-w-10rem">
                <template #body="{ data }">
                  {{ formatDate(data?.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                </template>
              </TableColumn>
              <TableColumn field="depTime" :header="t('worksheet.dep_time')" dataType="date" class="min-w-10rem">
                <template #body="{ data }">
                  {{ data.depTime }}
                </template>
              </TableColumn>
              <TableColumn field="arrDate" :header="t('worksheet.arrival_date')" dataType="date" class="min-w-10rem">
                <template #body="{ data }">
                  {{ formatDate(data.arrDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                </template>
              </TableColumn>
              <TableColumn field="arrTime" :header="t('worksheet.arrival_time')" dataType="date" class="min-w-10rem">
                <template #body="{ data }">
                  {{ data.arrTime }}
                </template>
              </TableColumn>
              <TableColumn field="fClass" :header="t('common.class')">
                <template #body="{ data }">
                  <span>{{ data.fClass }}</span>
                </template>
              </TableColumn>
            </DataTable>
            <div v-if="option.flightNoValid !== false" class="classes-available mt-4">
              <div class="mb-1">
                <div class="classes-available-header flex justify-content-between cursor-pointer">
                  <div class="flex align-items-center gap-10">
                    <div>{{ t('worksheet.class_available') }}</div>
                  </div>
                </div>
                <div v-show="slotProps.data.rClass">
                  <div class="classes-available-detail">
                    <div class="outbound-detail flex w-full" v-for="(flight, flightIndex) in slotProps.data.rClass" :key="'flight' + flightIndex">
                      <div class="title w-2 p-3">{{ t(`worksheet.${flight.flight?.toLowerCase()}`) }}</div>
                      <div class="title w-2 p-3">{{ flight.flightNo }}</div>
                      <div class="flex flex-wrap pb-5 gap-1 w-10">
                        <div class="p-2" v-for="(value, property) in flight.rClass" :key="'outbound' + property">
                          <div class="box-border">{{ property }}</div>
                          <div class="box-border">{{ value }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </DataTable>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.box-border {
  border: 1px solid #dee2e6;
  padding: 8px;
  text-align: center;
}
</style>
